import { useState } from "react";
import { Compass } from "lucide-react";
import { useLang } from "../i18n/LangContext";

// Διαδραστικός χάρτης θεωριών πάνω στην αυθεντική έγχρωμη γκραβούρα Bellin (1797).

const MAP_URL = '/images/bellin-1797-framed.jpg';
const FULL_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/88/IAN_0134_Bellin_1797_Ionian_Sea.jpg';

// Ποσοσταίες θέσεις πάνω στο κεντραρισμένο απόσπασμα πλαισίου (επαληθευμένες στον ίδιο τον χάρτη)
const SPOTS = {
  lefkada: { x: 21, y: 20 },
  ithaca: { x: 29, y: 43 },
  paliki: { x: 13, y: 45.5 },
  cephMain: { x: 34, y: 59 },
  zakynthos: { x: 37, y: 85.5 },
  echinades: { x: 66, y: 48 },
};

export const TheoryMap = () => {
  const { d, ui, lang } = useLang();
  const [active, setActive] = useState('trad');
  const THEORY_TABS = d.THEORY_TABS;
  const theory = THEORY_TABS.find((t) => t.key === active);
  const ithacaName = lang === 'en' ? 'Ithaca' : 'Ιθάκη';

  return (
    <section id="theorymap" data-testid="theorymap-section" className="scroll-mt-24 border-b border-line bg-surface/50 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10 flex items-start gap-4">
          <div className="rounded-full bg-anthra p-3 text-paper">
            <Compass className="h-5 w-5" />
          </div>
          <div>
            <p className="eyebrow">{ui.theoryEyebrow}</p>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl font-semibold tracking-tight text-ink">
              {ui.theoryTitle}
            </h2>
            <p className="mt-3 max-w-3xl text-base md:text-lg leading-relaxed text-muted-foreground">
              {ui.theoryDesc}
            </p>
          </div>
        </div>

        <div className="mb-8 flex flex-wrap gap-3" role="tablist" aria-label={ui.theoryTabsLabel}>
          {THEORY_TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={active === t.key}
              data-testid={`theory-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`rounded-full border px-6 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                active === t.key
                  ? 'border-aegean bg-aegean text-paper'
                  : 'border-line bg-paper text-foreground/70 hover:border-aegean/40 hover:text-aegean'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-start">
          <div className="relative overflow-hidden rounded-lg border border-line" data-testid="theorymap-canvas">
            <img
              src={MAP_URL}
              alt="Bellin 1797 coloured engraving with the theory placements"
              className="block h-auto w-full"
            />
            {Object.entries(theory.assign).map(([spotKey, name]) => {
              const s = SPOTS[spotKey];
              const isIthaca = name === ithacaName;
              return (
                <span
                  key={`${active}-${spotKey}-${lang}`}
                  data-testid={`map-label-${spotKey}`}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border px-2 py-0.5 font-serif shadow-sm backdrop-blur-sm transition-all duration-300 md:px-3 md:py-1 ${
                    isIthaca
                      ? 'border-terra bg-terra/90 text-sm font-bold text-paper md:text-xl'
                      : 'border-aegean/40 bg-paper/90 text-[10px] font-semibold text-ink md:text-sm'
                  }`}
                  style={{ left: `${s.x}%`, top: `${s.y}%` }}
                >
                  {isIthaca && (
                    <span className="absolute -inset-1.5 animate-ping rounded-full border border-terra/60" aria-hidden="true" />
                  )}
                  {name}
                </span>
              );
            })}
          </div>

          <div>
            <h3 className="font-serif text-2xl font-semibold tracking-tight text-ink">
              {theory.label}
            </h3>
            <p className="mt-3 text-base leading-relaxed text-foreground/85" data-testid="theorymap-summary">
              {theory.summary}
            </p>
            <p className="mt-6 text-xs text-muted-foreground">
              J.-N. Bellin (German ed.), «Karte von den Inseln St. Maura, Cephallenia, Zante», 1797 ·{' '}
              <a href={FULL_URL} target="_blank" rel="noopener noreferrer" data-testid="theorymap-source-link" className="font-semibold text-aegean hover:text-terra">
                {ui.theoryFullSize}
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
